<script setup lang="ts">
import type { RegionInfoProps, RegionInfoLinkClickedEmitProps } from '.';
import type { FunctionalComponent, SVGAttributes } from 'vue';

import TextLink from '../ui/text-link/TextLink.vue';
import RealStateAgentIcon from '~icons/material-symbols-light/real-estate-agent-outline-sharp';
import TrophyIcon from '~icons/material-symbols-light/trophy-outline-sharp';
import SupportAgentIcon from '~icons/material-symbols-light/support-agent-outline-sharp';
import QuickReferenceAllIcon from '~icons/material-symbols-light/quick-reference-all-outline-sharp';
import { slugify } from '~~/utils';

const props = defineProps<RegionInfoProps>();

const title = computed<string>(
	() => `Descubra o melhor de ${props.name} com a Pilar`
);
const link = computed<{ label: string; url: string }>(() => ({
	label: `Mais informações sobre ${props.name}`,
	url: `/bairros/${slugify(props.name || '')}`
}));

const emit = defineEmits<{
	(e: 'region-clicked', value: RegionInfoLinkClickedEmitProps): void;
}>();

const qualities: { icon: FunctionalComponent<SVGAttributes>; label: string }[] =
	[
		{
			icon: RealStateAgentIcon,
			label: 'Imóveis Exclusivos'
		},
		{
			icon: TrophyIcon,
			label: 'Imobiliárias líderes do mercado'
		},
		{
			icon: SupportAgentIcon,
			label: 'Acessoria e orientações nas transações'
		},
		{
			icon: QuickReferenceAllIcon,
			label: 'Análise de toda a documentação'
		}
	];

function handleClickLink() {
	emit('region-clicked', {
		ctaText: link.value.label,
		destinationUrl: link.value.url
	});
}
</script>

<template>
	<ContentBlock
		:title="title"
		title-as="h2"
		title-class="max-w-[940px] mx-auto"
		content-class="space-y-6 max-w-[940px] mx-auto"
	>
		<!-- eslint-disable-next-line vue/no-v-html -->
		<p
			v-for="(paragraph, index) in descriptionParagraphs"
			:key="`${name}-description-paragraph-${index}`"
			class="text-left text-primary"
			v-html="paragraph"
		/>

		<div
			class="flex flex-col gap-5 bg-white-dark-1 p-4 md:grid md:grid-cols-2 md:p-5"
		>
			<div
				v-for="quality in qualities"
				:key="`${name}-quality-${quality.label}`"
				class="flex items-center gap-2 self-stretch"
			>
				<component :is="quality.icon" class="size-5 text-primary" />
				<p class="text-sm font-normal leading-normal text-primary">
					{{ quality.label }}
				</p>
			</div>
		</div>
		<NuxtLink
			:to="link.url"
			class="flex cursor-pointer items-center gap-2 space-x-2 text-sm font-normal leading-normal text-primary"
			@click="handleClickLink"
		>
			<TextLink class="flex flex-row items-center gap-2" as="h4">
				{{ link.label }}
				<IconMaterialSymbolsLightNorthEast class="size-4 text-primary" />
			</TextLink>
		</NuxtLink>
	</ContentBlock>
</template>
